/**
 * Global Snackbar Styles
 * Theme-aware: card background, thin colored border, left color strip (full height), title + description, dismiss X.
 */

import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../theme/getTheme';

export interface SnackbarStylesOverrides {
  bottom?: number;
}

const DEFAULT_BOTTOM = Platform.OS === 'web' ? 24 : 32;
const BORDER_WIDTH = 1;
const ICON_STRIP_WIDTH = 40;
const BORDER_RADIUS = 12;
const PADDING_H = 16;
const PADDING_V = 16;

/** Very light desaturated strip: ~12% opacity. */
const opacity12 = (hex: string) => `${hex}1F`;

export const getSnackbarStyles = (
  theme: Theme,
  overrides?: SnackbarStylesOverrides
) => {
  const bottom = overrides?.bottom ?? DEFAULT_BOTTOM;

  return StyleSheet.create({
    container: {
      position: 'absolute',
      bottom,
      left: 20,
      right: 20,
      flexDirection: 'row',
      alignItems: 'stretch',
      borderRadius: BORDER_RADIUS,
      borderWidth: BORDER_WIDTH,
      paddingLeft: ICON_STRIP_WIDTH,
      paddingRight: PADDING_H,
      paddingVertical: PADDING_V,
      minHeight: 56,
      zIndex: 10005,
      elevation: 10,
      overflow: 'hidden',
      backgroundColor: theme.background.card,
      // Web: avoid compositor / containing-block issues so text and Lucide icons paint reliably.
      ...(Platform.OS === 'web'
        ? {
            position: 'fixed' as never,
          }
        : {}),
      ...(Platform.OS === 'web'
        ? { boxShadow: theme.shadows?.card ?? '0 2px 8px rgba(0, 0, 0, 0.08)' }
        : {
            shadowColor: theme.shadow,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.08,
            shadowRadius: 8,
            elevation: 4,
          }),
    },
    /** Full-height left strip (position absolute so it fills 100% height). */
    iconStrip: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: ICON_STRIP_WIDTH,
      justifyContent: 'center',
      alignItems: 'center',
    },
    iconStripInfo: {
      backgroundColor: opacity12(theme.info),
    },
    iconStripSuccess: {
      backgroundColor: opacity12(theme.success),
    },
    iconStripWarning: {
      backgroundColor: opacity12(theme.warning),
    },
    iconStripError: {
      backgroundColor: opacity12(theme.error),
    },
    containerInfo: {
      borderColor: theme.info + '99',
    },
    containerSuccess: {
      borderColor: theme.success + '99',
    },
    containerWarning: {
      borderColor: theme.warning + '99',
    },
    containerError: {
      borderColor: theme.error + '99',
    },
    content: {
      flex: 1,
      justifyContent: 'center',
      paddingLeft: 12,
      paddingTop: 2,
      paddingBottom: 2,
      paddingRight: 36,
    },
    title: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.black,
      marginBottom: 2,
      ...(Platform.OS === 'web' && { lineHeight: 22 }),
    },
    message: {
      fontSize: 14,
      fontWeight: '400',
      color: theme.gray[600],
      ...(Platform.OS === 'web' && { lineHeight: 20 }),
    },
    closeWrap: {
      position: 'absolute',
      right: 8,
      top: 8,
      width: 28,
      height: 28,
      borderRadius: 14,
      justifyContent: 'center',
      alignItems: 'center',
    },
    closePressed: {
      opacity: 0.7,
    },
    closeHover: {
      opacity: 0.85,
      backgroundColor: theme.gray[100],
    },
    actionWrap: {
      alignSelf: 'center',
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      marginRight: 28,
    },
    actionPressed: {
      opacity: 0.7,
    },
    actionHover: {
      opacity: 0.85,
      backgroundColor: theme.gray[100],
    },
    actionLabel: {
      fontSize: 14,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
  });
};

export type SnackbarStyles = ReturnType<typeof getSnackbarStyles>;
